// src/hooks/useCachedData.ts
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchCategories } from '../store/slices/categorySlice';
import { fetchMenuItems } from '../store/slices/menuItemSlice';
import {
    selectCategories,
    selectCategoriesHierarchy,
    selectCategoryLoading,
    selectCategoryLastFetchedAt,
    selectCategoryCurrentBranchId,
    selectMenuItems,
    selectMenuItemLoading,
    selectMenuItemLastFetchedAt,
    selectMenuItemCurrentBranchId,
} from '../store/selectors';

// Cache duration in milliseconds (5 minutes) - same as in slices
const CACHE_DURATION_MS = 5 * 60 * 1000;

interface UseCachedCategoriesOptions {
    branchId: string | undefined;
    forceRefresh?: boolean;
    skip?: boolean;
}

interface UseCachedMenuItemsOptions {
    branchId: string | undefined;
    forceRefresh?: boolean;
    skip?: boolean;
}

/**
 * Helper to calculate cache info
 */
const getCacheInfo = (lastFetchedAt: number | null, currentBranchId: string | null, branchId: string | undefined) => {
    if (!lastFetchedAt || !branchId) {
        return { isCached: false, cacheAgeSeconds: null, cacheAgeFormatted: null, lastFetchedAt: null };
    }

    const now = Date.now();
    const ageMs = now - lastFetchedAt;
    const ageSeconds = Math.round(ageMs / 1000);
    const isSameBranch = currentBranchId === branchId;
    const isCacheValid = ageMs < CACHE_DURATION_MS;

    // Format age nicely
    let cacheAgeFormatted: string;
    if (ageSeconds < 60) {
        cacheAgeFormatted = `${ageSeconds}s`;
    } else {
        const minutes = Math.floor(ageSeconds / 60);
        const seconds = ageSeconds % 60;
        cacheAgeFormatted = `${minutes}m ${seconds}s`;
    }

    return {
        isCached: isSameBranch && isCacheValid,
        cacheAgeSeconds: ageSeconds,
        cacheAgeFormatted,
        lastFetchedAt: new Date(lastFetchedAt).toLocaleTimeString(),
    };
};

/**
 * Hook to fetch and cache categories for a branch.
 * Will automatically use cached data if available and fresh.
 * 
 * @example
 * ```tsx
 * const { categories, isLoading, isCached, cacheAgeFormatted } = useCachedCategories({ branchId });
 * console.log(isCached ? `Using cache (${cacheAgeFormatted} old)` : 'Fresh data');
 * ```
 */
export const useCachedCategories = (options: UseCachedCategoriesOptions) => {
    const { branchId, forceRefresh = false, skip = false } = options;
    const dispatch = useAppDispatch();

    const categories = useAppSelector(selectCategories);
    const categoriesHierarchy = useAppSelector(selectCategoriesHierarchy);
    const isLoading = useAppSelector(selectCategoryLoading);
    const lastFetchedAt = useAppSelector(selectCategoryLastFetchedAt);
    const currentBranchId = useAppSelector(selectCategoryCurrentBranchId);

    const cacheInfo = getCacheInfo(lastFetchedAt, currentBranchId, branchId);

    useEffect(() => {
        if (!branchId || skip) return;
        dispatch(fetchCategories({ branchId, forceRefresh }));
    }, [branchId, forceRefresh, skip, dispatch]);

    return {
        categories,
        categoriesHierarchy,
        isLoading,
        ...cacheInfo,
        refetch: () => branchId && dispatch(fetchCategories({ branchId, forceRefresh: true })),
    };
};

/**
 * Hook to fetch and cache menu items for a branch.
 * Will automatically use cached data if available and fresh.
 * 
 * @example
 * ```tsx
 * const { menuItems, isLoading, isCached, cacheAgeFormatted } = useCachedMenuItems({ branchId });
 * console.log(isCached ? `Using cache (${cacheAgeFormatted} old)` : 'Fresh data');
 * ```
 */
export const useCachedMenuItems = (options: UseCachedMenuItemsOptions) => {
    const { branchId, forceRefresh = false, skip = false } = options;
    const dispatch = useAppDispatch();

    const menuItems = useAppSelector(selectMenuItems);
    const isLoading = useAppSelector(selectMenuItemLoading);
    const lastFetchedAt = useAppSelector(selectMenuItemLastFetchedAt);
    const currentBranchId = useAppSelector(selectMenuItemCurrentBranchId);

    const cacheInfo = getCacheInfo(lastFetchedAt, currentBranchId, branchId);

    useEffect(() => {
        if (!branchId || skip) return;
        dispatch(fetchMenuItems({ branchId, forceRefresh }));
    }, [branchId, forceRefresh, skip, dispatch]);

    return {
        menuItems,
        isLoading,
        ...cacheInfo,
        refetch: () => branchId && dispatch(fetchMenuItems({ branchId, forceRefresh: true })),
    };
};

/**
 * Combined hook to fetch both categories and menu items with caching.
 * 
 * @example
 * ```tsx
 * const { categories, menuItems, isLoading } = useCachedBranchData({ branchId });
 * ```
 */
export const useCachedBranchData = (options: { branchId: string | undefined; forceRefresh?: boolean }) => {
    const { branchId, forceRefresh = false } = options;

    const categoriesData = useCachedCategories({ branchId, forceRefresh });
    const menuItemsData = useCachedMenuItems({ branchId, forceRefresh });

    return {
        categories: categoriesData.categories,
        categoriesHierarchy: categoriesData.categoriesHierarchy,
        menuItems: menuItemsData.menuItems,
        isLoading: categoriesData.isLoading || menuItemsData.isLoading,
        categoriesCached: categoriesData.isCached,
        menuItemsCached: menuItemsData.isCached,
        refetchAll: () => {
            categoriesData.refetch();
            menuItemsData.refetch();
        },
    };
};
